import React from 'react';
import { CheckCircle, User, Download, Calendar, Link } from 'lucide-react';
import '../RegistrationFlow.css';

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const SuccessStep = ({
  event,
  registrationData,
  isDarkMode
}) => {
  // Mock confirmation information
  const confirmationInfo = {
    confirmationNumber: `REG-${Math.floor(100000 + Math.random() * 900000)}`,
    registrationDate: new Date().toISOString(),
    paymentAmount: registrationData.grandTotal || 0,
    startDate: event.startDate,
    endDate: event.endDate,
    location: event.location || 'Convention Center'
  };

  // Calculate participant counts
  const dancerCount = (registrationData.dancers && Array.isArray(registrationData.dancers)) 
    ? registrationData.dancers.length 
    : 0;
    
  const teacherCount = (registrationData.teachers && Array.isArray(registrationData.teachers)) 
    ? registrationData.teachers.length 
    : 0;
    
  const observerCount = (registrationData.observers && Array.isArray(registrationData.observers)) 
    ? registrationData.observers.length 
    : 0;
    
  const routineCount = (registrationData.routines && Array.isArray(registrationData.routines)) 
    ? registrationData.routines.length 
    : 0;

  // Print or download receipt (mock function)
  const downloadReceipt = () => {
    alert('Receipt download functionality would be implemented here.');
  };

  return (
    <div className="min-h-full w-full">
      {/* Success header */}
      <div className={`p-8 text-center ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
          isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-600'
        }`}>
          <CheckCircle className="h-8 w-8" />
        </div>
        <h2 className={`text-2xl font-bold mt-4 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>
          Registration Complete!
        </h2>
        <p className={`mt-2 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          Your registration for {event.name} has been confirmed
        </p>
      </div>
      
      {/* Main content */}
      <div className="p-4 md:p-6">
        <div className="max-w-3xl mx-auto">
          {/* Confirmation details */}
          <div className={`p-6 rounded-lg border ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          } mb-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Confirmation Details
            </h3>
            
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-dashed last:border-b-0">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Confirmation Number
                </span>
                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {confirmationInfo.confirmationNumber}
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-dashed last:border-b-0">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Registration Date
                </span>
                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatDate(confirmationInfo.registrationDate)}
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-dashed last:border-b-0">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Payment Amount
                </span>
                <span className={`font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {formatCurrency(confirmationInfo.paymentAmount)}
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b border-dashed last:border-b-0">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Event Date
                </span>
                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatDate(confirmationInfo.startDate)} - {formatDate(confirmationInfo.endDate)}
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:justify-between py-2">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Location
                </span>
                <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {confirmationInfo.location}
                </span>
              </div>
            </div>
          </div>
          
          {/* Registration summary */}
          <div className={`p-6 rounded-lg border ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          } mb-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Registration Summary
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg text-center ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <User className={`h-6 w-6 mx-auto mb-2 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-500'
                }`} />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Dancers
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {dancerCount}
                </p>
              </div>
              
              <div className={`p-4 rounded-lg text-center ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <User className={`h-6 w-6 mx-auto mb-2 ${
                  isDarkMode ? 'text-green-400' : 'text-green-500'
                }`} />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Teachers
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {teacherCount}
                </p>
              </div>
              
              <div className={`p-4 rounded-lg text-center ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <User className={`h-6 w-6 mx-auto mb-2 ${
                  isDarkMode ? 'text-orange-400' : 'text-orange-500'
                }`} />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Observers
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {observerCount}
                </p>
              </div>
              
              <div className={`p-4 rounded-lg text-center ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
              }`}>
                <Calendar className={`h-6 w-6 mx-auto mb-2 ${
                  isDarkMode ? 'text-pink-400' : 'text-pink-500'
                }`} />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Routines
                </p>
                <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {routineCount}
                </p>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="space-y-4">
            <button
              onClick={downloadReceipt}
              className={`w-full py-3 rounded-lg font-medium flex items-center justify-center ${
                isDarkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              <Download className="h-5 w-5 mr-2" />
              Download Receipt
            </button>
            
            <a
              href="/dashboard"
              className={`block w-full py-3 rounded-lg font-medium text-center ${
                isDarkMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <span className="flex items-center justify-center">
                <Link className="h-5 w-5 mr-2" />
                Go to Dashboard
              </span>
            </a>
          </div>
          
          {/* Additional info */}
          <div className={`mt-8 p-4 rounded-lg ${
            isDarkMode ? 'bg-blue-900/20 border border-blue-900/30' : 'bg-blue-50 border border-blue-200'
          }`}>
            <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
              A confirmation email has been sent to your registered email address. If you don't see it, please check your spam folder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessStep;
